import type { Copy } from "../types";

export const sk: Copy = {
    seo: {
      title: "Kalkulačka veku vozidla | kontrola podľa roka a mesiaca",
      description:
        "Skontrolujte presný vek vozidla podľa roku výroby a voliteľného mesiaca. Zobrazte roky, mesiace, dni, celkové dni a míľniky v jednom čistom nástroji.",
      keywords: [
        "kalkulačka veku vozidla",
        "vek auta",
        "vek vozidla podľa roka",
        "rok výroby auta",
        "mesiac výroby vozidla",
        "ako staré je moje auto",
        "kontrola veku vozidla",
        "kalkulačka veku RC",
        "kalkulačka dátumu vozidla",
        "kalkulačka veku auta podľa mesiaca",
        "alternatíva mParivahan",
        "alternatíva Parivahan",
        "kalkulačka veku vozidla RTO",
        "kontrola veku registrácie auta",
        "vek registrácie vozidla",
      ],
      faq: [
        {
          question: "Čo táto kalkulačka robí?",
          answer:
            "Vypočíta presný vek vozidla z vybraného roku výroby a voliteľného mesiaca a zobrazí roky, mesiace, dni aj informácie o míľnikoch.",
        },
        {
          question: "Prečo je mesiac voliteľný?",
          answer:
            "Voľba mesiaca spresňuje výpočet veku. Ak mesiac vynecháte, aplikácia použije január vybraného roka.",
        },
        {
          question: "Môžem vybrať budúci rok alebo mesiac?",
          answer:
            "Nie. Budúce roky sú zablokované a budúce mesiace sú vypnuté, keď je vybraný rok aktuálny.",
        },
      ],
    },
    headerTagline: "Pomôcka pre vozidlá",
    headerTitle: "Kalkulačka veku auta",
    reset: "Resetovať",
    switchToLight: "Prepnúť na svetlý režim",
    switchToDark: "Prepnúť na tmavý režim",
    languageLabel: "Jazyk",
    languageEnglish: "Angličtina",
    languageHindi: "Hindčina",
    heroEyebrow: "Vek auta, prehľadne",
    heroTitle: "Čistý nástroj na vek vozidla.",
    heroDescription: "Vyberte rok výroby. Mesiac pridajte len vtedy, keď chcete presnejší výsledok.",
    yearSelectLabel: "Rok výroby",
    monthSelectLabel: "Mesiac výroby",
    includeMonthLabel: "Zahrnúť konkrétny mesiac pre väčšiu presnosť",
    includeMonthNote: "Ak mesiac nezadáte, pri výpočte sa použije január.",
    yearOnlyBadge: "Len rok",
    yearMonthBadge: "Rok + mesiac",
    yearPlaceholder: "Vyberte rok",
    monthPlaceholder: "Vyberte mesiac",
    inputTitle: "Rok výroby",
    inputLabel: "Rok výroby",
    blockedDateNote: "Budúce roky sú zablokované.",
    result: "Výsledok",
    vehicleAgeLead: "Vaše vozidlo je",
    asOfToday: "K dnešnému dňu",
    years: "Roky",
    months: "Mesiace",
    days: "Dni",
    totalDays: (count) => `${count.toLocaleString("en-IN")} celkových dní`,
    ageLabels: { almostNew: "Takmer nové", modern: "Moderné", mature: "Zrelejšie", classic: "Klasické" },
    estimatedValue: {
      high: "Vysoký potenciál ďalšieho predaja",
      healthy: "Dobré udržanie hodnoty",
      moderate: "Mierne znehodnotenie",
      collector: "Môže rásť záujem zberateľov",
    },
    maintenanceTitle: "Údržba",
    maintenance: {
      routine: "Bežný servis a starostlivosť OEM",
      watch: "Sledujte brzdy, kvapaliny a pneumatiky",
      plan: "Plánujte opotrebenie a kontroly",
      specialized: "Špecializovaná údržba je dôležitejšia",
    },
    registrationTitle: "Registrácia",
    registration: {
      collector: "V niektorých regiónoch môže platiť zberateľský režim",
      classic: "V niektorých regiónoch môže platiť klasická registrácia",
      standard: "Počítajte so štandardnými obnovami a miestnymi kontrolami",
      regular: "Zvyčajne platí bežná registrácia",
    },
    upcomingMilestoneTitle: "Najbližší míľnik",
    milestoneTimelineTitle: "Časová os míľnikov",
    milestoneRemaining: ({ label, days }) => `${label} o ${days.toLocaleString("sk-SK")} dní`,
    reached: "Dosiahnuté",
    upcoming: "Najbližší",
    allMilestonesReachedTitle: "Všetky uvedené míľniky boli dosiahnuté",
    allMilestonesReachedBody: "Toto vozidlo už prekročilo rozsah míľnikov zobrazený v aplikácii.",
    copySummary: "Kopírovať súhrn",
    shareResult: "Zdieľať výsledok",
    resetMessage: "Kalkulačka bola resetovaná",
    summaryCopied: "Súhrn skopírovaný do schránky",
    clipboardFailed: "Prístup do schránky zlyhal",
    emptyTitle: "Na zobrazenie úplného prehľadu vyberte rok výroby",
    emptyDescription:
      "Po výbere roka uvidíte presný vek, celkový počet dní, poznámky k údržbe, klasický status aj načasovanie míľnikov.",
    shareTitle: "Kalkulačka veku vozidla",
    summary: ({ label, years, months, days, asOf }) =>
      `Moje vozidlo má dátum výroby ${label} a k ${asOf} je staré ${years} rokov, ${months} mesiacov a ${days} dní.`,
    milestones: {
      1: { label: "Prvý rok", note: "Užitočné pre záruku a plánovanie servisu." },
      3: { label: "Tri roky", note: "Bežný bod pre predaj a zmeny hodnoty." },
      5: { label: "Päť rokov", note: "Dobrá chvíľa na hlbší rozpočet údržby." },
      10: { label: "Desať rokov", note: "Staršie diely si zaslúžia väčšiu pozornosť." },
      15: { label: "Pätnásť rokov", note: "V mnohých regiónoch sa začína klasické zaobchádzanie." },
      25: { label: "Dvadsaťpäť rokov", note: "Môže byť zaujímavé pre zberateľov a dovoz." },
    },
  };
